import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import RsvpModal from "./components/RsvpModal";
import ThankYouScreen from "./components/ThankYouScreen";

/* ─── Types ──────────────────────────────────────────── */
type InvitationData = {
  id: string;
  tenant_id: string;
  title: string;
  event_title: string | null;
  host_names: string | null;
  event_type: string;
  event_date: string | null;
  event_time: string | null;
  venue_name: string | null;
  venue_address: string | null;
  google_maps_url: string | null;
  dress_code: string | null;
  gift_registry_url: string | null;
  cover_image_url: string | null;
  photo_url: string | null;
  music_url: string | null;
  music_title: string | null;
  client_music_url: string | null;
  client_music_name: string | null;
  gallery_urls: string[];
  theme_key: string;
  design_mode: string | null;
  template_id: string | null;
  custom_template_id: string | null;
  custom_template_url: string | null;
  invitation_mode: string | null;
  rsvp_enabled: boolean;
  rsvp_message: string | null;
  is_published: boolean;
  welcome_message: string | null;
  thank_you_message: string | null;
  ceremony_name: string | null;
  ceremony_address: string | null;
  ceremony_maps_url: string | null;
  ceremony_time: string | null;
  reception_name: string | null;
  reception_address: string | null;
  reception_maps_url: string | null;
  event_name: string | null;
  event_location: string | null;
  custom_message: string | null;
  text_color: string | null;
  text_style: string | null;
  show_guests: boolean;
};

type TenantData = {
  name: string;
  logo_url: string | null;
};

type TemplateData = {
  background_url: string | null;
  overlay_color: string | null;
  text_color: string;
};

type RsvpRow = {
  id: string;
  guest_name: string;
  attendance_status: string;
  guest_count: number;
};

/* ─── Theme system ───────────────────────────────────── */
export type Theme = {
  bg: string;
  surface: string;
  accent: string;
  accentText: string;
  text: string;
  textMuted: string;
  textFaint: string;
  border: string;
  inputBg: string;
  font: string;
  ornament: string;
};

export const THEMES: Record<string, Theme> = {
  classic: {
    bg:         "#FAF7F2",
    surface:    "#FFFFFF",
    accent:     "#B8935A",
    accentText: "#FFFFFF",
    text:       "#2C1F0E",
    textMuted:  "#7A6040",
    textFaint:  "#B8A080",
    border:     "#E8D9C0",
    inputBg:    "#FDFAF5",
    font:       "'Playfair Display', Georgia, serif",
    ornament:   "❧",
  },
  modern: {
    bg:         "#F8F8FA",
    surface:    "#FFFFFF",
    accent:     "#1A1A2E",
    accentText: "#FFFFFF",
    text:       "#0F0F1A",
    textMuted:  "#6B6B80",
    textFaint:  "#ABABBB",
    border:     "#E4E4EE",
    inputBg:    "#FAFAFA",
    font:       "'DM Sans', sans-serif",
    ornament:   "◆",
  },
  floral: {
    bg:         "#FDF6F8",
    surface:    "#FFFFFF",
    accent:     "#C2607A",
    accentText: "#FFFFFF",
    text:       "#2D1520",
    textMuted:  "#8B5060",
    textFaint:  "#C4909A",
    border:     "#F0D0D8",
    inputBg:    "#FFF8FA",
    font:       "'Cormorant Garamond', Georgia, serif",
    ornament:   "✿",
  },
  gold: {
    bg:         "#FAF8F2",
    surface:    "#FFFFFF",
    accent:     "#D4A850",
    accentText: "#FFFFFF",
    text:       "#2E2A1E",
    textMuted:  "#8B8050",
    textFaint:  "#C0B888",
    border:     "#E8E0C8",
    inputBg:    "#FDFBF5",
    font:       "'Playfair Display', Georgia, serif",
    ornament:   "◈",
  },
  sage: {
    bg:         "#F5F7F3",
    surface:    "#FFFFFF",
    accent:     "#5E7D60",
    accentText: "#FFFFFF",
    text:       "#1F2E1E",
    textMuted:  "#4F6B50",
    textFaint:  "#8FA88A",
    border:     "#D0DDD0",
    inputBg:    "#F7FBF6",
    font:       "'DM Sans', sans-serif",
    ornament:   "❖",
  },
  // Legacy aliases from admin panel
  dark: {
    bg:         "#0F172A",
    surface:    "#1E293B",
    accent:     "#C8A96E",
    accentText: "#0F172A",
    text:       "#F1F5F9",
    textMuted:  "#94A3B8",
    textFaint:  "#475569",
    border:     "#334155",
    inputBg:    "#1E293B",
    font:       "'DM Sans', sans-serif",
    ornament:   "◆",
  },
  celestial: {
    bg:         "#1E1B4B",
    surface:    "#2D2B6B",
    accent:     "#A5B4FC",
    accentText: "#1E1B4B",
    text:       "#E0E7FF",
    textMuted:  "#C7D2FE",
    textFaint:  "#818CF8",
    border:     "#3730A3",
    inputBg:    "#2D2B6B",
    font:       "'Cormorant Garamond', Georgia, serif",
    ornament:   "✦",
  },
};

/* ─── Countdown hook ─────────────────────────────────── */
function useCountdown(targetDate: string | null) {
  const [diff, setDiff] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);

  useEffect(() => {
    if (!targetDate) return;
    const target = new Date(targetDate).getTime();
    const tick = () => {
      const now = Date.now();
      const rem = target - now;
      if (rem <= 0) { setDiff({ days: 0, hours: 0, minutes: 0, seconds: 0 }); return; }
      setDiff({
        days:    Math.floor(rem / 86400000),
        hours:   Math.floor((rem % 86400000) / 3600000),
        minutes: Math.floor((rem % 3600000) / 60000),
        seconds: Math.floor((rem % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return diff;
}

/* ─── Music Player mini ──────────────────────────────── */
function MusicPlayerMini({
  url,
  title,
  accent,
  surface,
  border,
}: {
  url: string;
  title: string | null;
  accent: string;
  surface: string;
  border: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play().then(() => setPlaying(true)).catch(() => {}); }
  }, [playing]);

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setProgress((audio.currentTime / audio.duration) * 100);
  }, []);

  return (
    <div
      className="flex items-center gap-3 rounded-2xl px-4 py-3 border"
      style={{ backgroundColor: surface, borderColor: border }}
    >
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setPlaying(false)}
        loop
      />
      <button
        onClick={toggle}
        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 cursor-pointer"
        style={{ backgroundColor: accent + "20", color: accent }}
      >
        <i className={playing ? "ri-pause-fill" : "ri-play-fill"} />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium mb-1.5 truncate" style={{ color: "#444" }}>
          {title || "Música de fondo"}
        </p>
        <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: accent + "20" }}>
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${progress}%`, backgroundColor: accent }}
          />
        </div>
      </div>
      {playing && (
        <div className="flex items-end gap-0.5 shrink-0 h-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="w-1 rounded-full"
              style={{
                backgroundColor: accent,
                height: "100%",
                animation: `wave${i} 0.8s ease-in-out infinite`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Section card ───────────────────────────────────── */
function SectionCard({ children, surface, border }: { children: React.ReactNode; surface: string; border: string }) {
  return (
    <div className="rounded-3xl border overflow-hidden" style={{ backgroundColor: surface, borderColor: border }}>
      {children}
    </div>
  );
}

function Divider({ accent, ornament }: { accent: string; ornament: string }) {
  return (
    <div className="flex items-center gap-3 my-2">
      <div className="flex-1 h-px" style={{ backgroundColor: accent + "30" }} />
      <span className="text-sm" style={{ color: accent + "60" }}>{ornament}</span>
      <div className="flex-1 h-px" style={{ backgroundColor: accent + "30" }} />
    </div>
  );
}

/* ─── Location Card ──────────────────────────────────── */
function LocationCard({
  label, name, address, mapsUrl, time, theme, icon,
}: {
  label: string; name: string | null; address: string | null;
  mapsUrl: string | null; time?: string | null; theme: Theme; icon: string;
}) {
  if (!name && !address) return null;
  return (
    <SectionCard surface={theme.surface} border={theme.border}>
      <div className="px-6 py-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 flex items-center justify-center rounded-full" style={{ backgroundColor: theme.accent + "15", color: theme.accent }}>
            <i className={`${icon} text-sm`} />
          </span>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textFaint }}>{label}</p>
        </div>
        <div>
          {name && <p className="font-bold text-base mb-1" style={{ color: theme.text, fontFamily: theme.font }}>{name}</p>}
          {time && <p className="text-xs font-medium mb-1" style={{ color: theme.accent }}>{time}</p>}
          {address && <p className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>{address}</p>}
        </div>
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl font-semibold text-sm cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: theme.accent, color: theme.accentText }}>
            <i className="ri-navigation-line" />
            Cómo llegar
          </a>
        )}
      </div>
    </SectionCard>
  );
}

/* ─── Loading ────────────────────────────────────────── */
function InvitationLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FAF7F2" }}>
      <div className="text-center space-y-4">
        <div className="w-10 h-10 border-2 border-[#B8935A] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-[#B8935A] text-sm tracking-widest uppercase">Cargando invitación…</p>
      </div>
    </div>
  );
}

/* ─── Not Found ──────────────────────────────────────── */
function InvitationNotFound({ reason }: { reason?: "not_found" | "not_published" }) {
  const isNotPublished = reason === "not_published";
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "#FAF7F2" }}>
      <div className="text-center max-w-sm space-y-6">
        <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-[#B8935A]/10 border border-[#B8935A]/15">
          <i className={`${isNotPublished ? "ri-time-line" : "ri-mail-close-line"} text-4xl text-[#B8935A]/40`} />
        </div>
        <div className="space-y-3">
          <h1 className="text-[#2C1F0E] text-2xl font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {isNotPublished ? "Próximamente" : "Invitación no encontrada"}
          </h1>
          <p className="text-[#7A6040] text-sm leading-relaxed">
            {isNotPublished
              ? "Esta invitación se está preparando. Vuelve más tarde o contacta al organizador del evento."
              : "Este link puede ser incorrecto o la invitación fue eliminada."}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── Open overlay ───────────────────────────────────── */
function OpenOverlay({
  invitation, theme, bgImage, onOpen,
}: {
  invitation: InvitationData; theme: Theme; bgImage: string | null; onOpen: () => void;
}) {
  const displayTitle = invitation.event_title || invitation.event_name || invitation.title;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-end pb-12 sm:justify-center sm:pb-0 px-6"
      style={
        bgImage
          ? { backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center top" }
          : { backgroundColor: theme.bg }
      }
    >
      {bgImage && <div className="absolute inset-0 bg-black/50" />}
      <div className="relative text-center max-w-sm w-full space-y-7">
        {!bgImage && (
          <p className="text-4xl" style={{ color: theme.accent + "30", letterSpacing: "0.5em" }}>
            {theme.ornament}
          </p>
        )}

        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.4em] font-semibold"
            style={{ color: bgImage ? "rgba(255,255,255,0.75)" : theme.accent + "80" }}>
            {invitation.event_type}
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight"
            style={{
              color: bgImage ? "#fff" : theme.text,
              fontFamily: theme.font,
              textShadow: bgImage ? "0 2px 12px rgba(0,0,0,0.5)" : "none",
            }}>
            {displayTitle}
          </h1>
          {invitation.host_names && (
            <p className="text-sm" style={{ color: bgImage ? "rgba(255,255,255,0.8)" : theme.textMuted, fontFamily: theme.font }}>
              {invitation.host_names}
            </p>
          )}
          {invitation.event_date && (
            <p className="text-xs font-medium" style={{ color: bgImage ? "rgba(255,255,255,0.65)" : theme.textMuted }}>
              {new Date(invitation.event_date).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}
        </div>

        {/* CTA — user gesture here triggers music autoplay */}
        <button
          onClick={onOpen}
          className="w-full py-4 rounded-2xl font-bold text-sm cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap transition-all active:scale-95"
          style={{ backgroundColor: theme.accent, color: theme.accentText }}
        >
          <i className="ri-mail-open-line" />
          Abrir invitación
        </button>
        <p className="text-xs" style={{ color: bgImage ? "rgba(255,255,255,0.4)" : theme.textFaint }}>
          Toca para ver todos los detalles
        </p>
      </div>
    </div>
  );
}

/* ─── Guest list (show_guests=true) ─────────────────── */
function GuestList({ invitationId, theme }: { invitationId: string; theme: Theme }) {
  const [rsvps, setRsvps] = useState<RsvpRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("invitation_rsvps")
      .select("id, guest_name, attendance_status, guest_count")
      .eq("invitation_id", invitationId)
      .eq("attendance_status", "yes")
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => {
        setRsvps((data ?? []) as RsvpRow[]);
        setLoading(false);
      });
  }, [invitationId]);

  if (loading) return null;
  if (rsvps.length === 0) return null;

  const totalGuests = rsvps.reduce((a, b) => a + (b.guest_count || 1), 0);

  return (
    <SectionCard surface={theme.surface} border={theme.border}>
      <div className="px-6 py-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-full" style={{ backgroundColor: theme.accent + "15", color: theme.accent }}>
            <i className="ri-group-line text-sm" />
          </span>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textFaint }}>Quiénes van</p>
          <span className="ml-auto text-xs px-2.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: theme.accent + "15", color: theme.accent }}>
            {totalGuests} personas
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {rsvps.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: theme.accent + "10", color: theme.text, border: `1px solid ${theme.border}` }}
            >
              <i className="ri-user-smile-line text-[10px]" style={{ color: theme.accent }} />
              {r.guest_name}
              {r.guest_count > 1 && (
                <span className="text-[10px]" style={{ color: theme.textFaint }}>+{r.guest_count - 1}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </SectionCard>
  );
}

/* ─── Main page ──────────────────────────────────────── */
export default function InvitationPage() {
  const { public_slug } = useParams<{ public_slug: string }>();
  const [loading, setLoading]         = useState(true);
  const [invitation, setInvitation]   = useState<InvitationData | null>(null);
  const [tenant, setTenant]           = useState<TenantData | null>(null);
  const [templateData, setTemplateData] = useState<TemplateData | null>(null);
  const [notFoundReason, setNotFoundReason] = useState<"not_found" | "not_published">("not_found");
  const [opened, setOpened]           = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [showRsvp, setShowRsvp]       = useState(false);
  const [rsvpDone, setRsvpDone]       = useState(false);
  const audioRef                      = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!public_slug) { setLoading(false); return; }

      const { data: anyData } = await supabase
        .from("digital_invitations")
        .select("id, is_published")
        .eq("public_slug", public_slug)
        .maybeSingle();

      if (!anyData) { setNotFoundReason("not_found"); setLoading(false); return; }
      if (!anyData.is_published) { setNotFoundReason("not_published"); setLoading(false); return; }

      const { data } = await supabase
        .from("digital_invitations")
        .select(`
          id, tenant_id, title, event_title, event_name, host_names, event_type,
          event_date, event_time, venue_name, venue_address, google_maps_url,
          dress_code, gift_registry_url, cover_image_url, photo_url,
          music_url, music_title, client_music_url, client_music_name,
          gallery_urls, theme_key, design_mode, template_id,
          custom_template_id, custom_template_url, invitation_mode,
          rsvp_enabled, rsvp_message, is_published,
          welcome_message, thank_you_message, custom_message,
          ceremony_name, ceremony_address, ceremony_maps_url, ceremony_time,
          reception_name, reception_address, reception_maps_url,
          event_location, text_color, text_style, show_guests
        `)
        .eq("public_slug", public_slug)
        .eq("is_published", true)
        .maybeSingle();

      if (!data) { setNotFoundReason("not_found"); setLoading(false); return; }
      const inv = data as InvitationData;
      setInvitation(inv);

      // Load template background if design_mode = template and template_id exists
      const mode = inv.design_mode || inv.invitation_mode || "theme";
      if (mode === "template" && inv.template_id) {
        const { data: tplData } = await supabase
          .from("invitation_templates")
          .select("background_url, overlay_color, text_color")
          .eq("id", inv.template_id)
          .maybeSingle();
        if (tplData) setTemplateData(tplData as TemplateData);
      }

      // Load tenant
      if (inv.tenant_id) {
        const { data: tData } = await supabase
          .from("saas_tenants")
          .select("name, logo_url")
          .eq("id", inv.tenant_id)
          .maybeSingle();
        if (tData) setTenant(tData as TenantData);
      }

      setLoading(false);
    };
    load();
  }, [public_slug]);

  const countdown = useCountdown(invitation?.event_date ?? null);

  // Open handler — play music here (user gesture → browser allows autoplay)
  const handleOpen = useCallback(() => {
    setOpened(true);
    const musicUrl = invitation?.client_music_url || invitation?.music_url;
    if (musicUrl) {
      const audio = new Audio(musicUrl);
      audio.volume = 0.55;
      audio.loop = true;
      audio.play().catch(() => {});
      audioRef.current = audio;
    }
  }, [invitation]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
    };
  }, []);

  if (loading) return <InvitationLoading />;
  if (!invitation) return <InvitationNotFound reason={notFoundReason} />;

  const theme = THEMES[invitation.theme_key] ?? THEMES.classic;
  const gallery = Array.isArray(invitation.gallery_urls) ? invitation.gallery_urls : [];
  const displayTitle = invitation.event_title || invitation.event_name || invitation.title;
  const mode = invitation.design_mode || invitation.invitation_mode || "theme";

  // Determine background image
  let bgImage: string | null = null;
  let overlayColor = "rgba(0,0,0,0.35)";
  let heroTextColor = invitation.text_color || theme.text;

  if (mode === "photo") {
    bgImage = invitation.photo_url || invitation.cover_image_url || null;
    overlayColor = "rgba(0,0,0,0.35)";
    heroTextColor = invitation.text_color || "#ffffff";
  } else if (mode === "custom_template") {
    bgImage = invitation.custom_template_url || null;
    overlayColor = "rgba(0,0,0,0.30)";
    heroTextColor = invitation.text_color || "#ffffff";
  } else if (mode === "template" && templateData?.background_url) {
    bgImage = templateData.background_url;
    overlayColor = templateData.overlay_color || "rgba(0,0,0,0.40)";
    heroTextColor = templateData.text_color || "#ffffff";
  }

  const hasImageBg = !!bgImage;
  const textOnImage = hasImageBg;
  const effectiveTextColor = textOnImage ? heroTextColor : theme.text;
  const effectiveTextMuted = textOnImage ? "rgba(255,255,255,0.75)" : theme.textMuted;

  // Best music URL
  const musicUrl = invitation.client_music_url || invitation.music_url || null;
  const musicTitle = invitation.client_music_name || invitation.music_title || null;

  const formattedDate = invitation.event_date
    ? new Date(invitation.event_date).toLocaleDateString("es-ES", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      })
    : null;

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />

      <style>{`
        .animate-fadeIn { animation: fadeIn 0.6s ease-out both; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        @keyframes wave1 { 0%,100%{height:30%} 50%{height:100%} }
        @keyframes wave2 { 0%,100%{height:70%} 50%{height:30%} }
        @keyframes wave3 { 0%,100%{height:50%} 50%{height:90%} }
      `}</style>

      <div className="min-h-screen py-8 px-4" style={{ backgroundColor: theme.bg }}>

        {/* Open overlay */}
        {!opened && (
          <OpenOverlay invitation={invitation} theme={theme} bgImage={bgImage} onOpen={handleOpen} />
        )}

        {/* 9:16 card + content */}
        <div className="w-full max-w-[400px] mx-auto mb-4">

          {/* Hero card */}
          <div
            className="relative w-full rounded-3xl overflow-hidden mb-4"
            style={{
              aspectRatio: "9/16",
              backgroundColor: hasImageBg ? "#000" : theme.surface,
              border: `1px solid ${theme.border}`,
            }}
          >
            {/* Background layer */}
            {bgImage && (
              <div className="absolute inset-0">
                <img src={bgImage} alt="Fondo" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0" style={{ backgroundColor: overlayColor }} />
              </div>
            )}

            {/* Content */}
            <div
              className="relative z-10 h-full flex flex-col items-center justify-center px-8 py-10 text-center"
              style={!hasImageBg ? { background: `linear-gradient(135deg, ${theme.accent}08, transparent)` } : {}}
            >
              <p className="text-3xl mb-6" style={{ color: textOnImage ? "rgba(255,255,255,0.3)" : theme.accent + "30", letterSpacing: "0.4em" }}>
                {theme.ornament}
              </p>

              <span
                className="text-[10px] font-semibold uppercase tracking-[0.4em] px-3 py-1.5 rounded-full mb-4"
                style={{
                  backgroundColor: textOnImage ? "rgba(255,255,255,0.2)" : theme.accent + "15",
                  color: textOnImage ? "rgba(255,255,255,0.9)" : theme.accent,
                  backdropFilter: textOnImage ? "blur(8px)" : "none",
                }}
              >
                {invitation.event_type}
              </span>

              <h1 className="text-3xl font-bold leading-tight mb-3"
                style={{
                  color: effectiveTextColor,
                  fontFamily: theme.font,
                  textShadow: textOnImage ? "0 2px 16px rgba(0,0,0,0.6)" : "none",
                }}>
                {displayTitle}
              </h1>

              {invitation.host_names && (
                <p className="text-sm mb-2" style={{ color: effectiveTextMuted, fontFamily: theme.font }}>
                  {invitation.host_names}
                </p>
              )}

              <Divider accent={textOnImage ? "rgba(255,255,255,0.4)" : theme.accent} ornament={theme.ornament} />

              {formattedDate && (
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-semibold capitalize" style={{ color: effectiveTextColor }}>{formattedDate}</p>
                  {invitation.event_time && (
                    <p className="text-xs" style={{ color: effectiveTextMuted }}>{invitation.event_time}</p>
                  )}
                </div>
              )}

              {(invitation.ceremony_name || invitation.venue_name || invitation.event_location) && (
                <div className="mt-3 flex items-center gap-1.5">
                  <i className="ri-map-pin-2-line text-xs" style={{ color: textOnImage ? "rgba(255,255,255,0.6)" : theme.accent }} />
                  <p className="text-xs" style={{ color: effectiveTextMuted }}>
                    {invitation.ceremony_name || invitation.venue_name || invitation.event_location}
                  </p>
                </div>
              )}

              {countdown && (countdown.days > 0 || countdown.hours > 0) && (
                <div className="grid grid-cols-4 gap-2 mt-5 w-full">
                  {[
                    { val: countdown.days, label: "Días" },
                    { val: countdown.hours, label: "Hrs" },
                    { val: countdown.minutes, label: "Min" },
                    { val: countdown.seconds, label: "Seg" },
                  ].map(({ val, label }) => (
                    <div key={label} className="rounded-2xl py-2.5 text-center"
                      style={{
                        backgroundColor: textOnImage ? "rgba(255,255,255,0.15)" : theme.accent + "12",
                        border: `1px solid ${textOnImage ? "rgba(255,255,255,0.25)" : theme.accent + "25"}`,
                        backdropFilter: textOnImage ? "blur(8px)" : "none",
                      }}>
                      <p className="text-lg font-bold tabular-nums" style={{ color: effectiveTextColor, fontFamily: theme.font }}>
                        {String(val).padStart(2, "0")}
                      </p>
                      <p className="text-[9px] uppercase tracking-wider mt-0.5" style={{ color: effectiveTextMuted }}>{label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* RSVP button */}
              {invitation.rsvp_enabled && !rsvpDone && (
                <button
                  onClick={() => setShowRsvp(true)}
                  className="mt-6 w-full py-3.5 rounded-2xl font-bold text-sm cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap transition-all active:scale-95"
                  style={{
                    backgroundColor: textOnImage ? "rgba(255,255,255,0.95)" : theme.accent,
                    color: textOnImage ? theme.accent : theme.accentText,
                    backdropFilter: textOnImage ? "blur(4px)" : "none",
                  }}
                >
                  <i className="ri-checkbox-circle-line" />
                  Confirmar asistencia
                </button>
              )}

              {rsvpDone && (
                <div className="mt-6 w-full py-3 rounded-2xl font-medium text-sm flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: textOnImage ? "rgba(255,255,255,0.15)" : theme.accent + "15",
                    color: textOnImage ? "rgba(255,255,255,0.9)" : theme.accent,
                    backdropFilter: textOnImage ? "blur(8px)" : "none",
                  }}>
                  <i className="ri-check-line" />
                  ¡Asistencia confirmada!
                </div>
              )}
            </div>
          </div>

          {/* Below-card content */}
          <div className="space-y-4 animate-fadeIn">

            {/* Welcome message */}
            {(invitation.welcome_message || invitation.custom_message) && (
              <SectionCard surface={theme.surface} border={theme.border}>
                <div className="px-6 py-6 text-center">
                  <Divider accent={theme.accent} ornament={theme.ornament} />
                  <p className="text-sm leading-relaxed italic mt-3" style={{ color: theme.textMuted, fontFamily: theme.font }}>
                    &ldquo;{invitation.welcome_message || invitation.custom_message}&rdquo;
                  </p>
                </div>
              </SectionCard>
            )}

            {/* Ceremony */}
            <LocationCard
              label="Ceremonia"
              name={invitation.ceremony_name || invitation.venue_name}
              address={invitation.ceremony_address || invitation.venue_address}
              mapsUrl={invitation.ceremony_maps_url || invitation.google_maps_url}
              time={invitation.ceremony_time || invitation.event_time}
              theme={theme}
              icon="ri-map-pin-2-line"
            />

            {/* Reception */}
            <LocationCard
              label="Recepción"
              name={invitation.reception_name}
              address={invitation.reception_address}
              mapsUrl={invitation.reception_maps_url}
              theme={theme}
              icon="ri-map-pin-3-line"
            />

            {/* Dress code */}
            {invitation.dress_code && (
              <SectionCard surface={theme.surface} border={theme.border}>
                <div className="px-6 py-5 flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-2xl shrink-0"
                    style={{ backgroundColor: theme.accent + "15", color: theme.accent }}>
                    <i className="ri-shirt-line text-lg" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: theme.textFaint }}>Código de vestimenta</p>
                    <p className="font-semibold text-sm" style={{ color: theme.text }}>{invitation.dress_code}</p>
                  </div>
                </div>
              </SectionCard>
            )}

            {/* Gift registry */}
            {invitation.gift_registry_url && (
              <SectionCard surface={theme.surface} border={theme.border}>
                <div className="px-6 py-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-2xl shrink-0"
                      style={{ backgroundColor: theme.accent + "15", color: theme.accent }}>
                      <i className="ri-gift-2-line text-lg" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: theme.textFaint }}>Mesa de regalos</p>
                      <p className="font-semibold text-sm" style={{ color: theme.text }}>Ver lista de deseos</p>
                    </div>
                  </div>
                  <a href={invitation.gift_registry_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap"
                    style={{ backgroundColor: theme.accent + "15", color: theme.accent }}>
                    <i className="ri-external-link-line" />
                    Ver
                  </a>
                </div>
              </SectionCard>
            )}

            {/* Music player (manual control after open) */}
            {musicUrl && opened && (
              <MusicPlayerMini
                url={musicUrl}
                title={musicTitle}
                accent={theme.accent}
                surface={theme.surface}
                border={theme.border}
              />
            )}

            {/* Gallery */}
            {gallery.length > 0 && (
              <SectionCard surface={theme.surface} border={theme.border}>
                <div className="px-6 pt-5 pb-2">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full"
                      style={{ backgroundColor: theme.accent + "15", color: theme.accent }}>
                      <i className="ri-image-2-line text-sm" />
                    </span>
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textFaint }}>Galería</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1 px-1 pb-1">
                  {gallery.slice(0, 9).map((url, idx) => (
                    <button key={idx} onClick={() => setLightboxIdx(idx)}
                      className="relative overflow-hidden cursor-pointer group" style={{ paddingBottom: "100%" }}>
                      <img src={url} alt={`Galería ${idx + 1}`}
                        className="absolute inset-0 w-full h-full object-cover object-top transition-transform group-hover:scale-105" />
                      {idx === 8 && gallery.length > 9 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <p className="text-white font-bold text-lg">+{gallery.length - 9}</p>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <div className="h-3" />
              </SectionCard>
            )}

            {/* Guest list (show_guests=true) */}
            {invitation.show_guests && (
              <GuestList invitationId={invitation.id} theme={theme} />
            )}

            {/* Thank you after RSVP */}
            {rsvpDone && (
              <SectionCard surface={theme.surface} border={theme.border}>
                <div className="px-6 py-7">
                  <ThankYouScreen
                    invitation={invitation}
                    theme={theme}
                    onAddGuest={() => setRsvpDone(false)}
                  />
                </div>
              </SectionCard>
            )}

            {/* Share row */}
            {!rsvpDone && (
              <div className="flex gap-2.5">
                <button
                  onClick={() => { navigator.clipboard.writeText(window.location.href).catch(() => {}); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-xs font-semibold cursor-pointer whitespace-nowrap border"
                  style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.textMuted }}
                >
                  <i className="ri-link-m" />
                  Copiar link
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${displayTitle} — ${window.location.href}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-xs font-semibold cursor-pointer whitespace-nowrap"
                  style={{ backgroundColor: "#22c55e18", color: "#16a34a" }}
                >
                  <i className="ri-whatsapp-line" />
                  WhatsApp
                </a>
              </div>
            )}

            {/* Footer */}
            <div className="text-center py-4 space-y-1.5">
              {tenant?.logo_url && (
                <img src={tenant.logo_url} alt={tenant.name || ""} className="h-7 mx-auto object-contain" />
              )}
              {tenant?.name && <p className="text-xs" style={{ color: theme.textFaint }}>{tenant.name}</p>}
              <p className="text-[10px]" style={{ color: theme.textFaint + "80" }}>
                Creado con <span className="font-semibold">Pixory AI</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RSVP Modal */}
      {showRsvp && (
        <RsvpModal
          invitation={invitation}
          theme={theme}
          onClose={() => setShowRsvp(false)}
          onSubmitted={() => { setShowRsvp(false); setRsvpDone(true); }}
        />
      )}

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div className="fixed inset-0 bg-black/95 z-[90] flex items-center justify-center px-4"
          onClick={() => setLightboxIdx(null)}>
          <button className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/60 hover:text-white bg-white/10 rounded-full cursor-pointer"
            onClick={() => setLightboxIdx(null)}>
            <i className="ri-close-line text-xl" />
          </button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-white/60 hover:text-white bg-white/10 rounded-full cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => Math.max(0, (i ?? 0) - 1)); }}>
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <img src={gallery[lightboxIdx]} alt={`Galería ${lightboxIdx + 1}`}
            className="max-w-full max-h-[85vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-white/60 hover:text-white bg-white/10 rounded-full cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => Math.min(gallery.length - 1, (i ?? 0) + 1)); }}>
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40 text-xs">
            {lightboxIdx + 1} / {gallery.length}
          </p>
        </div>
      )}
    </>
  );
}